import { useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { motion } from "framer-motion";

export const SoundToggle = () => {
  const [on, setOn] = useState(false);
  const audioRef = useRef(null);

  const start = () => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);

    const len = ctx.sampleRate * 3;
    const bedBuf = ctx.createBuffer(1, len, ctx.sampleRate);
    const bedData = bedBuf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      bedData[i] = last * 3.5;
    }
    const bed = ctx.createBufferSource();
    bed.buffer = bedBuf;
    bed.loop = true;
    const bedFilter = ctx.createBiquadFilter();
    bedFilter.type = "lowpass";
    bedFilter.frequency.value = 320;
    const bedGain = ctx.createGain();
    bedGain.gain.value = 0.45;
    bed.connect(bedFilter);
    bedFilter.connect(bedGain);
    bedGain.connect(master);
    bed.start();

    let alive = true;
    const pop = () => {
      if (!alive) return;
      const dur = 0.015 + Math.random() * 0.05;
      const popBuf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
      const popData = popBuf.getChannelData(0);
      for (let i = 0; i < popData.length; i++) {
        popData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / popData.length, 2.2);
      }
      const src = ctx.createBufferSource();
      src.buffer = popBuf;
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = 900 + Math.random() * 2600;
      bp.Q.value = 1.2;
      const g = ctx.createGain();
      g.gain.value = 0.12 + Math.random() * 0.4;
      src.connect(bp);
      bp.connect(g);
      g.connect(master);
      src.start();
      setTimeout(pop, 50 + Math.random() * 480);
    };
    pop();

    master.gain.linearRampToValueAtTime(0.14, ctx.currentTime + 1.8);
    audioRef.current = {
      ctx,
      master,
      stopBed: () => {
        alive = false;
        try {
          bed.stop();
        } catch (e) {}
      },
    };
  };

  const stop = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const { ctx, master } = audio;
    master.gain.cancelScheduledValues(ctx.currentTime);
    master.gain.setValueAtTime(master.gain.value, ctx.currentTime);
    master.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.6);
    setTimeout(() => {
      audio.stopBed();
      ctx.close();
    }, 700);
    audioRef.current = null;
  };

  const toggle = () => {
    if (on) {
      stop();
      setOn(false);
    } else {
      start();
      setOn(true);
    }
  };

  return (
    <div className="fixed bottom-6 left-6 z-50 flex items-center gap-3">
      <motion.button
        data-testid="ambience-toggle-button"
        onClick={toggle}
        whileTap={{ scale: 0.9 }}
        aria-label={on ? "Mute fire ambience" : "Light the fire ambience"}
        className={`flex h-12 w-12 items-center justify-center rounded-full border backdrop-blur-md transition-colors duration-300 ${
          on
            ? "border-gold bg-gold/15 text-gold-glow shadow-[0_0_30px_rgba(226,177,93,0.3)]"
            : "border-gold/30 bg-obsidian/70 text-sand hover:text-gold"
        }`}
      >
        {on ? (
          <Volume2 className="h-4 w-4 animate-flicker" />
        ) : (
          <VolumeX className="h-4 w-4" />
        )}
      </motion.button>
      <span
        data-testid="ambience-label"
        className={`font-cinzel text-[10px] tracking-[0.3em] transition-colors duration-500 ${
          on ? "text-gold/80" : "text-ash"
        }`}
      >
        {on ? "FIRE LIT" : "LIGHT THE FIRE"}
      </span>
    </div>
  );
};
